/**
 * Voom Analytics Tracker
 *
 * Sends user behavior events to the CEO dashboard's analytics endpoint.
 * Batches events in memory and flushes every 2 seconds via sendBeacon.
 * Completely silent, never blocks UI or shows errors to users.
 */

// ── Types ─────────────────────────────────────────────────────────────────

interface TrackerEvent {
  eventType: string;
  productId?: number | null;
  vendorId?: number | null;
  userId?: number | null;
  visitorId: string;
  sessionId: string;
  referrer: string;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  country: string;
  city: string;
  deviceType: "mobile" | "tablet" | "desktop";
  browser: string;
  os: string;
  screenWidth: number;
  pageUrl: string;
  pageTitle: string;
  metadata: Record<string, unknown>;
}

interface TrackEventOptions {
  eventType: string;
  productId?: number;
  vendorId?: number;
  userId?: number;
  metadata?: Record<string, unknown>;
}

// ── State ─────────────────────────────────────────────────────────────────

let trackerUrl = "";
let currentUserId: number | null = null;
let visitorId = "";
let sessionId = "";
let lastActivity = 0;
let queue: TrackerEvent[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let initialized = false;

const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

// ── Helpers ───────────────────────────────────────────────────────────────

function rand() {
  return Math.random().toString(36).slice(2, 8);
}

function getOrCreateVisitorId(): string {
  try {
    const existing = localStorage.getItem("voom_vid");
    if (existing) return existing;
    const id = `v_${Date.now().toString(36)}_${rand()}`;
    localStorage.setItem("voom_vid", id);
    return id;
  } catch {
    return `v_${Date.now().toString(36)}_${rand()}`;
  }
}

function getOrCreateSessionId(): string {
  try {
    const now = Date.now();
    const existing = sessionStorage.getItem("voom_sid");
    const lastTs = Number(sessionStorage.getItem("voom_sid_ts") || "0");

    // If session exists and hasn't expired, reuse it
    if (existing && now - lastTs < SESSION_TIMEOUT_MS) {
      sessionStorage.setItem("voom_sid_ts", String(now));
      return existing;
    }

    // New session
    const id = `s_${now.toString(36)}_${rand()}`;
    sessionStorage.setItem("voom_sid", id);
    sessionStorage.setItem("voom_sid_ts", String(now));
    return id;
  } catch {
    return `s_${Date.now().toString(36)}_${rand()}`;
  }
}

function refreshSession() {
  const now = Date.now();
  if (now - lastActivity > SESSION_TIMEOUT_MS) {
    // Session expired, start new one
    sessionId = getOrCreateSessionId();
    trackEvent({ eventType: "session_start" });
  }
  lastActivity = now;
  try {
    sessionStorage.setItem("voom_sid_ts", String(now));
  } catch {
    /* */
  }
}

function getDeviceType(): "mobile" | "tablet" | "desktop" {
  const w = window.innerWidth;
  if (w < 768) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

function getBrowser(): string {
  const ua = navigator.userAgent;
  if (ua.includes("Edg/")) return "Edge";
  if (ua.includes("Chrome/") && !ua.includes("Edg/")) return "Chrome";
  if (ua.includes("Safari/") && !ua.includes("Chrome")) return "Safari";
  if (ua.includes("Firefox/")) return "Firefox";
  return "Other";
}

function getOS(): string {
  const ua = navigator.userAgent;
  if (ua.includes("Android")) return "Android";
  if (/iPad|iPhone|iPod/.test(ua)) return "iOS";
  if (ua.includes("Windows")) return "Windows";
  if (ua.includes("Mac OS")) return "macOS";
  if (ua.includes("Linux")) return "Linux";
  return "Other";
}

function getLocation(): { country: string; city: string } {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    // Map common Ghana timezones
    if (tz.includes("Accra")) return { country: "Ghana", city: "Accra" };
    if (tz.includes("Africa/")) {
      const city = tz.split("/").pop()?.replace(/_/g, " ") || "";
      return { country: "Ghana", city };
    }
    return { country: "Unknown", city: "" };
  } catch {
    return { country: "Unknown", city: "" };
  }
}

function getUTMParams(): {
  source: string | null;
  medium: string | null;
  campaign: string | null;
} {
  try {
    const params = new URLSearchParams(window.location.search);
    return {
      source: params.get("utm_source"),
      medium: params.get("utm_medium"),
      campaign: params.get("utm_campaign"),
    };
  } catch {
    return { source: null, medium: null, campaign: null };
  }
}

function getReferrer(): string {
  try {
    const ref = document.referrer;
    if (!ref) return "";
    // Exclude same-site referrers
    const refHost = new URL(ref).hostname;
    if (refHost === window.location.hostname) return "";
    return refHost;
  } catch {
    return "";
  }
}

// ── Flush ─────────────────────────────────────────────────────────────────

function flush() {
  if (queue.length === 0 || !trackerUrl) return;

  const batch = queue.splice(0);
  const payload = JSON.stringify(batch);

  try {
    // Prefer sendBeacon, survives page unload
    if (navigator.sendBeacon) {
      const blob = new Blob([payload], { type: "application/json" });
      navigator.sendBeacon(trackerUrl, blob);
    } else {
      fetch(trackerUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: payload,
        keepalive: true,
      }).catch(() => {
        /* silent */
      });
    }
  } catch {
    // Silent, never block UI
  }
}

// ── Public API ────────────────────────────────────────────────────────────

export function initVoomTracker(options?: {
  trackerUrl?: string;
  userId?: number;
}): void {
  if (initialized) return;
  if (typeof window === "undefined") return;

  try {
    trackerUrl =
      options?.trackerUrl || "https://voom-ceo-dashboard.replit.app/api/track";
    if (options?.userId) currentUserId = options.userId;

    visitorId = getOrCreateVisitorId();
    sessionId = getOrCreateSessionId();
    lastActivity = Date.now();

    // Flush every 2 seconds
    flushTimer = setInterval(flush, 2000);

    // Fire session_start
    trackEvent({ eventType: "session_start" });

    // Fire initial page_view with route context
    trackPageView();

    // Register session end / page leave handlers (handles beforeunload + visibilitychange + flush)
    // NOTE: These listeners are registered ONCE here, do not add duplicates in initVoomTracker
    initSessionEndTracking();

    initialized = true;
  } catch {
    // Silent init failure
  }
}

export function trackEvent(options: TrackEventOptions): void {
  if (typeof window === "undefined") return;

  try {
    refreshSession();

    const utm = getUTMParams();
    const location = getLocation();

    const event: TrackerEvent = {
      eventType: options.eventType,
      productId: options.productId ?? null,
      vendorId: options.vendorId ?? null,
      userId: options.userId ?? currentUserId,
      visitorId,
      sessionId,
      referrer: getReferrer(),
      utmSource: utm.source,
      utmMedium: utm.medium,
      utmCampaign: utm.campaign,
      country: location.country,
      city: location.city,
      deviceType: getDeviceType(),
      browser: getBrowser(),
      os: getOS(),
      screenWidth: window.innerWidth,
      pageUrl: window.location.pathname,
      pageTitle: document.title,
      metadata: options.metadata ?? {},
    };

    queue.push(event);
  } catch {
    // Silent, never crash the app
  }
}

export function setTrackerUserId(userId: number): void {
  currentUserId = userId;
}

let _prevPath: string | null = null;
let _sessionStartTime = Date.now();
let _pagesViewed = 0;
let _lastPageViewTime = Date.now();

/**
 * Track page views with route context + referrer + UTM + device.
 * These fields also flow to the external CEO dashboard via the tracker
 * queue, but we need them in Supabase metadata too (via /api/beacon)
 * so SQL analytics queries can segment by channel / OS / referrer.
 */
export function trackPageView(): void {
  _pagesViewed++;
  _lastPageViewTime = Date.now();
  const utm = getUTMParams();
  trackEvent({
    eventType: "page_view",
    metadata: {
      path: typeof window !== "undefined" ? window.location.pathname : null,
      referrer: typeof document !== "undefined" ? document.referrer : null,
      prevPath: _prevPath,
      utmSource: utm.source,
      utmMedium: utm.medium,
      utmCampaign: utm.campaign,
      os: getOS(),
      browser: getBrowser(),
      deviceType: getDeviceType(),
    },
  });
  if (typeof window !== "undefined") {
    _prevPath = window.location.pathname;
  }
}

/**
 * Session end / page leave tracking.
 * sendBeacon → /api/beacon (real endpoint, not tRPC).
 * visibilitychange rate-limited to 30s to avoid noise from tab switches.
 *
 * IMPORTANT: This function registers ALL beforeunload/visibilitychange
 * listeners. initVoomTracker() must NOT register duplicates.
 */
let _hiddenSince = 0;
let _sessionEndRegistered = false;
export function initSessionEndTracking(): void {
  if (typeof window === "undefined") return;
  if (_sessionEndRegistered) return; // Guard against duplicate registration
  _sessionEndRegistered = true;

  // On page close: flush pending events + send session_end beacon
  window.addEventListener("beforeunload", () => {
    // Flush queued events first (fire-and-forget)
    try {
      flush();
    } catch {
      /* */
    }
    // Then send session_end beacon
    try {
      const blob = new Blob(
        [
          JSON.stringify({
            eventType: "page_leave",
            visitorId,
            sessionId,
            userId: currentUserId,
            metadata: {
              action: "session_end",
              lastPath: window.location.pathname,
              sessionDurationMs: Date.now() - _sessionStartTime,
              pagesViewed: _pagesViewed,
            },
          }),
        ],
        { type: "application/json" }
      );
      navigator.sendBeacon("/api/beacon", blob);
    } catch {
      /* */
    }
  });

  // On tab hide: flush + track "hidden since" timestamp
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      _hiddenSince = Date.now();
      try {
        flush();
      } catch {
        /* */
      }
    } else if (_hiddenSince > 0) {
      const hiddenMs = Date.now() - _hiddenSince;
      _hiddenSince = 0;
      // Only log if they were actually gone (30s+), not just checking WhatsApp
      if (hiddenMs >= 30_000) {
        trackEvent({
          eventType: "page_view",
          metadata: {
            action: "returned_from_leave",
            path: window.location.pathname,
            hiddenMs,
          },
        });
      }
    }
  });
}

/**
 * Capture mechanic interest for re-engagement.
 * Stores search queries and viewed vehicle makes in sessionStorage
 * for the "more like this" algorithm, and in the server for future
 * email re-engagement campaigns.
 */
export function captureInterest(
  type: "search" | "view_make" | "view_category",
  value: string
): void {
  if (!value || typeof window === "undefined") return;
  try {
    // Store locally for session-level personalization
    const key = `voom_interests_${type}`;
    const existing = JSON.parse(
      sessionStorage.getItem(key) || "[]"
    ) as string[];
    if (!existing.includes(value)) {
      existing.unshift(value);
      sessionStorage.setItem(key, JSON.stringify(existing.slice(0, 10)));
    }
    // Also persist to server for email re-engagement (non-blocking)
    fetch("/api/trpc/captureInterest", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ json: { visitorId, interestType: type, value } }),
      keepalive: true,
    }).catch(() => {
      /* silent */
    });
  } catch {
    /* */
  }
}
